import React from 'react';
import { observer } from 'mobx-react';
import { useNavigate, useLocation } from 'react-router-dom';

import { themeService } from '@services/ThemeService';
import { modalService } from '@components/Modal';
import { searchService } from '@services/SearchService';
import { apiService } from '@services/ApiService';
import { onlineService } from '@services/OnlineService';
import { syncService } from '@services/SyncService';

const SettingsDialog = React.lazy(() => import('../../pages/dialogs/SettingsDialog'));

import './_nav.scss';

// Module-level: makes the /api/note/search-config fetch one-shot across (re)mounts.
let searchSemanticConfigLoaded = false;

function BrandImpl(): JSX.Element {
  const navigate = useNavigate();
  const [recent, setRecent] = React.useState(false);
  const timerRef = React.useRef<number | undefined>(undefined);

  const online = onlineService.isOnline;
  const syncing = syncService.status === 'actualizing';

  // While online (and not syncing) the logo is green for 2s, then returns to blue.
  // Hovering the logo reveals green again (see _nav.scss). Pending/offline are
  // always visible and take precedence over the green/blue online states.
  React.useEffect(() => {
    window.clearTimeout(timerRef.current);
    if (online && !syncing) {
      setRecent(true);
      timerRef.current = window.setTimeout(() => setRecent(false), 2000);
    } else {
      setRecent(false);
    }
    return () => window.clearTimeout(timerRef.current);
  }, [online, syncing]);

  let connClass = 'conn-online';
  if (!online) connClass = 'conn-offline';
  else if (syncing) connClass = 'conn-pending';
  else if (recent) connClass = 'conn-success';

  return (
    <div className="brand" onClick={() => navigate('/')} role="link" title="Go to dashboard">
      <span className={`brand-mark ${connClass}`}>N</span>
      <span className="brand-text">Notes <em>&amp;</em> You</span>
    </div>
  );
}

const Brand = observer(BrandImpl);

function NewNoteButton(): JSX.Element {
  const navigate = useNavigate();
  return (
    <button
      className="btn btn-primary"
      onClick={() => navigate('/n/new')}
      title="New note"
    >
      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
      <span className="btn-label">New note</span>
    </button>
  );
}

function SearchBoxImpl(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = React.useRef<HTMLInputElement>(null);

  const active = searchService.isActive;
  React.useEffect(() => {
    if (active && location.pathname !== '/notes') {
      navigate('/notes', { replace: true });
    }
  }, [active, location.pathname, navigate]);

  React.useEffect(() => {
    if (searchService.focusRequest > 0) inputRef.current?.focus();
  }, [searchService.focusRequest]);

  React.useEffect(() => {
    if (searchSemanticConfigLoaded) return;
    searchSemanticConfigLoaded = true;
    void apiService.get<{ semanticAvailable: boolean }>('/api/note/search-config')
      .then(cfg => searchService.setSemanticAvailable(!!cfg.semanticAvailable))
      .catch(() => { /* non-fatal: exact search still works */ });
  }, []);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (e.key === '/' && el && !['INPUT', 'TEXTAREA'].includes(el.tagName) && !el.isContentEditable) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const onClear = () => {
    searchService.clear();
    inputRef.current?.focus();
  };

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      searchService.update(inputRef.current?.value ?? '');
    } else if (e.key === 'Escape' && active) {
      searchService.clear();
      inputRef.current?.blur();
    }
  };

  const semantic = searchService.semanticAvailable;

  return (
    <div className={`search${active ? ' active' : ''}`}>
      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
      <input
        ref={inputRef}
        type="text"
        placeholder={searchService.useSemantic ? "Semantic search — describe what you're looking for…" : "Search notes, tags, content…"}
        autoComplete="off"
        value={searchService.query}
        onChange={e => searchService.update(e.target.value)}
        onKeyDown={onInputKeyDown}
      />
      {semantic && (
        <button
          type="button"
          className={`search-semantic${searchService.useSemantic ? ' active' : ''}`}
          title={searchService.useSemantic ? 'Semantic search on (click for exact match)' : 'Exact match on (click for semantic search)'}
          aria-label="Toggle semantic search"
          aria-pressed={searchService.useSemantic}
          onClick={() => searchService.toggleSemantic()}
        >
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3l1.9 4.6L18.5 9.5l-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3z" />
            <path d="M18.5 14l.9 2.1 2.1.9-2.1.9-.9 2.1-.9-2.1-2.1-.9 2.1-.9.9-2.1z" />
          </svg>
        </button>
      )}
      {active
        ? <button className="search-clear" title="Clear search" aria-label="Clear search" onClick={onClear}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        : <kbd className="kbd">/</kbd>}
    </div>
  );
}

const SearchBox = observer(SearchBoxImpl);

class NavBar extends React.Component {
  private _toggleTheme = () => {
    themeService.toggle();
  };

  private _openSettings = () => {
    void modalService.show<Record<string, never>, void>(SettingsDialog as unknown as React.ComponentClass<Record<string, never>>, {});
  };

  render(): JSX.Element {
    return (
      <header className="topbar">
        <div className="tb-left">
          <Brand />
        </div>

        <div className="tb-center">
          <SearchBox />
        </div>

        <div className="tb-right">
          <NewNoteButton />
          <button className="icon-btn" title="Toggle theme" aria-label="Toggle theme" onClick={this._toggleTheme}>
            <svg className="ico-moon" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg>
            <svg className="ico-sun" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4.5" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>
          </button>
          <button className="icon-btn" title="Settings" aria-label="Settings" onClick={this._openSettings}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
          </button>
        </div>
      </header>
    );
  }
}

export default NavBar;
